import type { Capability, MeResponse, StaffRole } from '@ucp/web/lib';

export type PupilRiskCapability = Extract<Capability, `pupil_risk_${string}`>;

/**
 * Pupil risk roles per capability (PRO-3220 Phase 3, ADR-018 PRO-3220 Phase 3 amendment).
 * Mirrors the server map in `apps/api/src/auth/capabilities.ts` (asserted by
 * `apps/api/test/pupilRiskAccessModel.test.ts`). UX only: the server re-checks every call, and
 * every capability is held at one school. Never `L6_PLATFORM_ADMIN`.
 */
export const PUPIL_RISK_CAPABILITY_ROLES: Readonly<
  Record<PupilRiskCapability, readonly StaffRole[]>
> = {
  pupil_risk_read: ['SENCO', 'DSL', 'PRINCIPAL', 'PUPIL_SUPPORT_STAFF'],
  pupil_risk_restricted_read: ['SENCO', 'DSL', 'PRINCIPAL'],
  pupil_risk_author: ['SENCO', 'PUPIL_SUPPORT_STAFF'],
  pupil_risk_approve: ['SENCO', 'PRINCIPAL'],
  pupil_risk_register_manage: ['SENCO'],
  pupil_risk_restrict: ['SENCO', 'DSL'],
  pupil_risk_register_read: ['SENCO', 'DSL', 'PRINCIPAL'],
  pupil_risk_export: ['DSL', 'PRINCIPAL'],
  pupil_risk_mode_propose: ['SENCO'],
  pupil_risk_mode_confirm: ['PRINCIPAL'],
};

/** Every role with a pupil risk capability: the `/pupil-risk/*` RouteGuard and the chrome. */
export const PUPIL_RISK_ROUTE_ROLES: readonly StaffRole[] = [
  ...new Set(Object.values(PUPIL_RISK_CAPABILITY_ROLES).flat()),
];

export type PupilRiskAccess = Readonly<Record<PupilRiskCapability, boolean>> & {
  readonly schoolId: string;
};

/** What the caller may do at the active school. */
export function pupilRiskAccess(
  user: Pick<MeResponse, 'roles'>,
  schoolId: string,
): PupilRiskAccess {
  const at = (roles: readonly StaffRole[]) =>
    user.roles.some((r) => r.schoolId === schoolId && roles.includes(r.role));
  const out = { schoolId } as Record<string, unknown>;
  for (const [cap, roles] of Object.entries(PUPIL_RISK_CAPABILITY_ROLES)) out[cap] = at(roles);
  return out as PupilRiskAccess;
}
